import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';
import * as THREE from 'three';

export class CircleLayouter extends BaseLayouter {
    public readonly defaultRadius: number = 5.0;
    public readonly defaultFixedSpacing: number = 1.0;
    public readonly defaultOffsetY: number = 0;
    
    constructor() {
        super(
            'circle',
            'Arranges objects in a circle with both Fixed and Uniform spacing modes',
            {
                circleFixedSpacing: 1.0,
                circleRadius: 5.0,
                circleOffsetY: 0,
                alignmentMode: AlignmentMode.Aligned
            },
            '1d', // 1D layouter (circular curve)
            [SpacingMode.Fixed, SpacingMode.Distribute] // Supports both spacing modes
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const offsetY = this.getParameter(layoutData, 'circleOffsetY', this.defaultOffsetY);
        const spacingMode = this.getParameter(layoutData, 'spacingMode', SpacingMode.Fixed);
        
        let radius: number;
        
        if (spacingMode === SpacingMode.Fixed) {
            // FIXED SPACING: Calculate radius needed for fixed arc spacing
            const fixedSpacing = this.getParameter(layoutData, 'circleFixedSpacing', this.defaultFixedSpacing);
            const totalArcLength = (totalCount - 1) * fixedSpacing;
            radius = totalArcLength / (2 * Math.PI);
            
            //console.log(`🔧 Circle Fixed Spacing: ${fixedSpacing} → radius ${radius.toFixed(2)}`);
        } else {
            // UNIFORM SPACING: Use fixed radius, calculate spacing
            radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
            
            //console.log(`🔧 Circle Uniform Spacing: radius ${radius}`);
        }
        
        // Position ALL objects equally around the circle
        const angle = (index / totalCount) * 2 * Math.PI;
        
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        
        return new Vector3(x, offsetY, z);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate parameter t for this object on the circle
        const t = index / totalCount; // Parameter from 0 to 1
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (curve movement direction) using numerical differentiation
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getCirclePointAt(t1, layoutData);
        const p2 = this.getCirclePointAt(t2, layoutData);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        
        // 2. Calculate normal vector (curvature direction) using second derivative
        const tangent1 = this.getCircleTangentAt(t1, layoutData);
        const tangent2 = this.getCircleTangentAt(t2, layoutData);
        let normal = new Vector3(tangent2.x - tangent1.x, tangent2.y - tangent1.y, tangent2.z - tangent1.z);
        
        // Handle degenerate case (should not happen for circles, but safety first)
        if (normal.length() < 0.001) {
            // Use perpendicular vector to tangent (pointing toward circle center)
            const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
            const angle = t * 2 * Math.PI;
            normal = new Vector3(-Math.cos(angle), 0, -Math.sin(angle));
        }
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to circle plane)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the curve direction (uses normal - curvature direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to curve (uses tangent - movement direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to circle plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - use tangent direction as fallback
                desiredForward = tangent.clone().normalize();
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // Check if desired forward is parallel to world up
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            
            if (forwardUpDot > parallelThreshold) {
                // Forward is parallel to world up - use tangent direction as forward
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // Normal case: forward is not parallel to up
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Convert orthogonal coordinate system to Euler angles (degrees)
        // Create rotation matrix from the orthogonal basis vectors
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(
            new THREE.Vector3(finalRight.x, finalRight.y, finalRight.z),
            new THREE.Vector3(finalUp.x, finalUp.y, finalUp.z),
            new THREE.Vector3(finalForward.x, finalForward.y, finalForward.z)
        );
        
        // Extract Euler angles from rotation matrix
        const euler = new THREE.Euler();
        euler.setFromRotationMatrix(matrix, 'XYZ');
        
        // Convert to degrees and return as Vector3
        return new Vector3(
            euler.x * 180 / Math.PI,
            euler.y * 180 / Math.PI,
            euler.z * 180 / Math.PI
        );
    }
    
    validateCircleParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        return radius > 0;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        return super.validateParameters(layoutData) && this.validateCircleParameters(layoutData);
    }
    
    // Calculate circumference
    getCircumference(layoutData: LayoutData): number {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        return 2 * Math.PI * radius;
    }
    
    // Calculate optimal spacing between objects
    getOptimalSpacing(objectCount: number, layoutData: LayoutData): number {
        const circumference = this.getCircumference(layoutData);
        return circumference / objectCount;
    }
    
    // Clone method
    clone(): CircleLayouter {
        return new CircleLayouter();
    }
    
    // Factory method
    static create(): CircleLayouter {
        return new CircleLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'circleFixedSpacing', this.defaultFixedSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const circumference = 2 * Math.PI * radius;
        const adjustedTotal = objectCount - 1; // Exclude first object at origin
        return circumference / adjustedTotal;
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate parameter t for this object on the circle
        const t = index / totalCount; // Parameter from 0 to 1
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (curve movement direction) using numerical differentiation
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getCirclePointAt(t1, layoutData);
        const p2 = this.getCirclePointAt(t2, layoutData);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        
        // 2. Calculate normal vector (curvature direction) using second derivative
        const tangent1 = this.getCircleTangentAt(t1, layoutData);
        const tangent2 = this.getCircleTangentAt(t2, layoutData);
        let normal = new Vector3(tangent2.x - tangent1.x, tangent2.y - tangent1.y, tangent2.z - tangent1.z);
        
        // Handle degenerate case (should not happen for circles, but safety first)
        if (normal.length() < 0.001) {
            // Use perpendicular vector to tangent (pointing toward circle center)
            const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
            const angle = t * 2 * Math.PI;
            normal = new Vector3(-Math.cos(angle), 0, -Math.sin(angle));
        }
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to circle plane)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the curve direction (uses normal - curvature direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to curve (uses tangent - movement direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to circle plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - use tangent direction as fallback
                desiredForward = tangent.clone().normalize();
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // Check if desired forward is parallel to world up
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            
            if (forwardUpDot > parallelThreshold) {
                // Forward is parallel to world up - use tangent direction as forward
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // Normal case: forward is not parallel to up
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Create quaternion from orthogonal coordinate system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Helper method: Get circle position at parameter t
     */
    private getCirclePointAt(t: number, layoutData: LayoutData): Vector3 {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const offsetY = this.getParameter(layoutData, 'circleOffsetY', this.defaultOffsetY);
        
        const angle = t * 2 * Math.PI;
        return new Vector3(
            Math.cos(angle) * radius,
            offsetY,
            Math.sin(angle) * radius
        );
    }
    
    /**
     * Helper method: Get tangent vector at parameter t using numerical differentiation
     */
    private getCircleTangentAt(t: number, layoutData: LayoutData): Vector3 {
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getCirclePointAt(t1, layoutData);
        const p2 = this.getCirclePointAt(t2, layoutData);
        return new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
    }
    
    /**
     * Calculate Frenet frame for circle curve (LEGACY - kept for reference)
     * This implements proper 1D curve mathematics from the documentation
     */
    private calculateCircleFrenetFrame(t: number, layoutData: LayoutData): {
        tangent: Vector3,
        normal: Vector3,
        binormal: Vector3
    } {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        
        // Circle parameterization: angle from 0 to 2π
        const angle = t * 2 * Math.PI;
        
        // Tangent vector: derivative of circle position (movement direction)
        // For circle: x = r*cos(θ), z = r*sin(θ), y = constant
        // dx/dt = -r*sin(θ) * (2π), dz/dt = r*cos(θ) * (2π)
        const tangent = new Vector3(
            -Math.sin(angle),  // Tangent in X direction
            0,                 // No Y movement
            Math.cos(angle)    // Tangent in Z direction
        ).normalize();
        
        // Normal vector: points toward center of circle (curvature direction)
        // For circle: always points radially inward
        const normal = new Vector3(
            -Math.cos(angle),  // Points toward center (X)
            0,                 // No Y component
            -Math.sin(angle)   // Points toward center (Z)
        ).normalize();
        
        // Binormal vector: tangent × normal (perpendicular to circle plane)
        // For horizontal circle, this points up in Y
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        return { tangent, normal, binormal };
    }

    /**
     * Create debug visualization for the circle path
     */
    public createDebugVisualization(layoutData: LayoutData): THREE.Group | null {
        // Fallback method - use estimated object count
        return this.createDebugVisualizationWithObjectCount(layoutData, 8);
    }

    /**
     * Create debug visualization with REAL object count
     */
    public createDebugVisualizationWithObjectCount(layoutData: LayoutData, objectCount: number): THREE.Group | null {
        const debugGroup = new THREE.Group();
        debugGroup.name = 'CircleLayouter_Debug';

        const offsetY = this.getParameter(layoutData, 'circleOffsetY', this.defaultOffsetY);
        const spacingMode = this.getParameter(layoutData, 'spacingMode', SpacingMode.Fixed);
        
        let radius: number;
        
        if (spacingMode === SpacingMode.Fixed) {
            // FIXED SPACING: Calculate radius needed for fixed arc spacing using REAL OBJECT COUNT
            const fixedSpacing = this.getParameter(layoutData, 'circleFixedSpacing', this.defaultFixedSpacing);
            const adjustedTotal = objectCount - 1;
            const totalArcLength = adjustedTotal * fixedSpacing;
            radius = totalArcLength / (2 * Math.PI);
        } else {
            // UNIFORM SPACING: Use fixed radius
            radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        }
        
        if (!radius || radius <= 0) {
            return null;
        }
        
        // Apply Y offset to center
        const center = new Vector3(0, 0, 0); // Circle centered at origin
        const actualCenter = new Vector3(center.x, center.y + offsetY, center.z);

        // Create circle curve
        const segments = 64;
        const points: THREE.Vector3[] = [];
        
        // Generate circle points in XZ plane
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            
            const point = new THREE.Vector3(
                actualCenter.x + x,
                actualCenter.y,
                actualCenter.z + z
            );
            
            points.push(point);
        }
        
        // Create circle line
        const circleGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const circleMaterial = new THREE.LineBasicMaterial({ 
            color: 0x0088ff,  // Blue for circle path
            linewidth: 3,
            transparent: true,
            opacity: 0.8
        });
        
        const debugCircle = new THREE.Line(circleGeometry, circleMaterial);
        debugCircle.name = 'DebugCirclePath';
        
        // Add center point indicator
        const centerGeometry = new THREE.SphereGeometry(Math.max(radius * 0.05, 0.1));
        const centerMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xffff00,  // Yellow for center
            transparent: true,
            opacity: 0.7
        });
        const centerSphere = new THREE.Mesh(centerGeometry, centerMaterial);
        centerSphere.position.set(actualCenter.x, actualCenter.y, actualCenter.z);
        centerSphere.name = 'CircleCenter';
        
        // Add position markers around the circle
        const numMarkers = 8;
        for (let i = 0; i < numMarkers; i++) {
            const angle = (i / numMarkers) * Math.PI * 2;
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            
            const markerPos = new THREE.Vector3(
                actualCenter.x + x,
                actualCenter.y,
                actualCenter.z + z
            );
            
            const markerGeometry = new THREE.SphereGeometry(0.1);
            const markerMaterial = new THREE.MeshBasicMaterial({ 
                color: 0x00ff88,  // Green-cyan for circle markers
                transparent: true,
                opacity: 0.8
            });
            const marker = new THREE.Mesh(markerGeometry, markerMaterial);
            marker.position.copy(markerPos);
            marker.name = `CircleMarker_${i}`;
            
            debugGroup.add(marker);
        }
        
        debugGroup.add(debugCircle);
        debugGroup.add(centerSphere);
        
        return debugGroup;
    }

    /**
     * Create an arrow helper
     */
    private createArrow(position: Vector3, direction: Vector3, size: number, color: number): THREE.Group {
        const arrowGroup = new THREE.Group();
        
        // Arrow shaft
        const shaftGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, size)
        ]);
        const shaftMaterial = new THREE.LineBasicMaterial({ color: color, linewidth: 2 });
        const shaft = new THREE.Line(shaftGeometry, shaftMaterial);
        
        // Arrow head
        const headGeometry = new THREE.ConeGeometry(size * 0.3, size * 0.4, 6);
        const headMaterial = new THREE.MeshBasicMaterial({ color: color });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        head.position.set(0, 0, size);
        head.rotateX(-Math.PI / 2);
        
        arrowGroup.add(shaft);
        arrowGroup.add(head);
        
        // Position and orient the arrow
        arrowGroup.position.set(position.x, position.y, position.z);
        
        // Orient arrow to point in direction
        const quaternion = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(direction.x, direction.y, direction.z)
        );
        arrowGroup.setRotationFromQuaternion(quaternion);
        
        return arrowGroup;
    }
}

export class CircleOutsideLayouter extends BaseLayouter {
    public readonly defaultRadius: number = 5.0;
    public readonly defaultOffsetY: number = 0;
    
    constructor() {
        super(
            'circle_outside',
            'Arranges objects in a circular pattern with objects facing outward',
            {
                circleRadius: 5.0,
                circleOffsetY: 0
            }
        );
    }
    
        calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const offsetY = this.getParameter(layoutData, 'circleOffsetY', this.defaultOffsetY);
        
        // CONSISTENT FIRST POSITION: Index 0 always at origin (0,0,0)
        if (index === 0) {
            return new Vector3(0, offsetY, 0);
        }
        
        // Remaining objects arranged in circle (starting from index 1)
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        const x = Math.cos(angle) * radius;
        const y = offsetY;
        const z = Math.sin(angle) * radius;
        
        return new Vector3(x, y, z);
    }

    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        // CONSISTENT FIRST ORIENTATION: Index 0 faces forward (no rotation)
        if (index === 0) {
            return new Vector3(0, 0, 0);
        }
        
        // Objects face outward from center
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        const yRotation = this.radiansToDegrees(angle); // Face outward
        return new Vector3(0, yRotation, 0);
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        return super.validateParameters(layoutData) && radius > 0;
    }
    
    // Clone method
    clone(): CircleOutsideLayouter {
        return new CircleOutsideLayouter();
    }
    
    // Factory method
    static create(): CircleOutsideLayouter {
        return new CircleOutsideLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        // This layouter uses circleRadius, not spacing-based parameters
        return 1.0; // Default spacing
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // This layouter doesn't support uniform spacing, return fixed spacing
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const circumference = 2 * Math.PI * radius;
        return circumference / objectCount;
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        // Objects face outward from center - convert existing logic to quaternion
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        
        // Face outward from center
        const outward = new Vector3(Math.cos(angle), 0, Math.sin(angle));
        const up = new Vector3(0, 1, 0);
        
        return Quaternion.lookDir(outward, up);
    }
}

export class CircleInsideLayouter extends BaseLayouter {
    public readonly defaultRadius: number = 5.0;
    public readonly defaultOffsetY: number = 0;
    
    constructor() {
        super(
            'circle_inside',
            'Arranges objects in a circular pattern with objects facing inward (same as circle)',
            {
                circleRadius: 5.0,
                circleOffsetY: 0
            }
        );
    }
    
        calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const offsetY = this.getParameter(layoutData, 'circleOffsetY', this.defaultOffsetY);
        
        // CONSISTENT FIRST POSITION: Index 0 always at origin (0,0,0)
        if (index === 0) {
            return new Vector3(0, offsetY, 0);
        }
        
        // Remaining objects arranged in circle (starting from index 1)
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        const x = Math.cos(angle) * radius;
        const y = offsetY;
        const z = Math.sin(angle) * radius;
        
        return new Vector3(x, y, z);
    }

    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        // CONSISTENT FIRST ORIENTATION: Index 0 faces forward (no rotation)
        if (index === 0) {
            return new Vector3(0, 0, 0);
        }
        
        // Objects face inward toward center (same as CircleLayouter)
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        const yRotation = this.radiansToDegrees(angle) + 180; // Face center
        return new Vector3(0, yRotation, 0);
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        return super.validateParameters(layoutData) && radius > 0;
    }
    
    // Clone method
    clone(): CircleInsideLayouter {
        return new CircleInsideLayouter();
    }
    
    // Factory method
    static create(): CircleInsideLayouter {
        return new CircleInsideLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        // This layouter uses circleRadius, not spacing-based parameters  
        return 1.0; // Default spacing
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // This layouter doesn't support uniform spacing, return fixed spacing
        const radius = this.getParameter(layoutData, 'circleRadius', this.defaultRadius);
        const circumference = 2 * Math.PI * radius;
        return circumference / objectCount;
    }
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        // Objects face inward to center - same as normal circle layout
        const adjustedIndex = index - 1;
        const adjustedTotal = totalCount - 1;
        const angle = (adjustedIndex / adjustedTotal) * 2 * Math.PI;
        
        // Calculate tangent along circle (for inward facing, same as aligned circle)
        const tangentX = -Math.sin(angle);
        const tangentZ = Math.cos(angle);
        const tangent = new Vector3(tangentX, 0, tangentZ);
        const up = new Vector3(0, 1, 0);
        
        return Quaternion.lookDir(tangent, up);
    }
} 